import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminModeProvider } from './context/AdminModeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { WishlistProvider } from './context/WishlistContext';
import { useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import { preventClickjacking, initSessionTimeout } from './utils/security';

import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import RegionSelector from './components/RegionSelector';
import Landing from './pages/Landing';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Checkout from './pages/Checkout';
import Search from './pages/Search';
import OrderConfirmation from './pages/OrderConfirmation';
import About from './pages/About';
import Contact from './pages/Contact';
import Wishlist from './pages/Wishlist';
import MyOrders from './pages/MyOrders';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Signup from './pages/Signup';
import CollectionDetail from './pages/CollectionDetail';
import Shipping from './pages/Shipping';
import FAQ from './pages/FAQ';
import SizeGuide from './pages/SizeGuide';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Sustainability from './pages/Sustainability';
import Careers from './pages/Careers';
import AdminSignIn from './pages/AdminSignIn';
import AdminDashboard from './pages/AdminDashboard';
import AdminProductsList from './pages/AdminProductsList';
import AdminProductNew from './pages/AdminProductNew';
import AdminProductEdit from './pages/AdminProductEdit';
import AdminProductStocks from './pages/AdminProductStocks';
import AdminCategories from './pages/AdminCategories';
import AdminCollections from './pages/AdminCollections';
import AdminOrders from './pages/AdminOrders';
import { useAdminMode } from './context/AdminModeContext';
import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const RegionAutoOpen = ({ loading, regionSelected, onOpen }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (loading) return;
    if (regionSelected) return;
    if (pathname !== '/home') return;
    const t = setTimeout(() => onOpen(), 1000);
    return () => clearTimeout(t);
  }, [loading, regionSelected, pathname, onOpen]);

  return null;
};

// Layout wrapper for main site pages
const MainLayout = ({ children, onOpenRegionSelector }) => {
  return (
    <>
      <Header onOpenRegionSelector={onOpenRegionSelector} />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      <CartDrawer />
    </>
  );
};

const RequireAdmin = ({ children }) => {
  const { adminModeEnabled } = useAdminMode();
  if (!adminModeEnabled) return <Navigate to="/admin" replace />;
  return children;
};

const RequireCustomerAuth = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center text-sm text-gray-600">
        Loading...
      </div>
    );
  }

  if (!currentUser || currentUser.isAnonymous) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent('/checkout')}`} replace />;
  }

  return children;
};

const AppShell = () => {
  const { userDoc, loading } = useAuth();
  const [showRegionSelector, setShowRegionSelector] = useState(false);

  useEffect(() => {
    preventClickjacking();
    initSessionTimeout(30 * 60 * 1000, () => {
      console.log('Session timeout - clearing sensitive data');
      sessionStorage.clear();
    });
  }, []);

  const openRegionSelector = useCallback(() => {
    setShowRegionSelector(true);
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RegionAutoOpen
        loading={loading}
        regionSelected={!!userDoc?.regionSelected}
        onOpen={openRegionSelector}
      />
      <div className="App min-h-screen flex flex-col">
        <Routes>
          {/* Landing page - no header/footer - ALWAYS shows first */}
          <Route path="/" element={<Landing />} />

          {/* Admin pages - no header/footer */}
          <Route path="/admin" element={<AdminSignIn />} />
          <Route
            path="/admin/dashboard"
            element={
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            }
          />

          {/* Admin product routes */}
          <Route
            path="/admin/products"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminProductsList />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/products/new"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminProductNew />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/products/:id/edit"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminProductEdit />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/products/:id/stocks"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminProductStocks />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminCategories />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/collections"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminCollections />
                </MainLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <RequireAdmin>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <AdminOrders />
                </MainLayout>
              </RequireAdmin>
            }
          />

          {/* Main site pages - with header/footer */}
          <Route path="/home" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Home /></MainLayout>} />
          <Route path="/shop/:category" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Shop /></MainLayout>} />
          <Route path="/product/:id" element={<MainLayout onOpenRegionSelector={openRegionSelector}><ProductDetail /></MainLayout>} />
          <Route path="/collections/:slug" element={<MainLayout onOpenRegionSelector={openRegionSelector}><CollectionDetail /></MainLayout>} />
          <Route path="/wishlist" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Wishlist /></MainLayout>} />
          <Route path="/orders" element={<MainLayout onOpenRegionSelector={openRegionSelector}><MyOrders /></MainLayout>} />
          <Route path="/profile" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Profile onOpenRegionSelector={openRegionSelector} /></MainLayout>} />
          <Route path="/login" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Login /></MainLayout>} />
          <Route path="/signup" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Signup /></MainLayout>} />
          <Route
            path="/checkout"
            element={
              <RequireCustomerAuth>
                <MainLayout onOpenRegionSelector={openRegionSelector}>
                  <Checkout />
                </MainLayout>
              </RequireCustomerAuth>
            }
          />
          <Route path="/search" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Search /></MainLayout>} />
          <Route path="/order-confirmation" element={<MainLayout onOpenRegionSelector={openRegionSelector}><OrderConfirmation /></MainLayout>} />

          {/* Info pages */}
          <Route path="/about" element={<MainLayout onOpenRegionSelector={openRegionSelector}><About /></MainLayout>} />
          <Route path="/contact" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Contact /></MainLayout>} />
          <Route path="/shipping" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Shipping /></MainLayout>} />
          <Route path="/faq" element={<MainLayout onOpenRegionSelector={openRegionSelector}><FAQ /></MainLayout>} />
          <Route path="/size-guide" element={<MainLayout onOpenRegionSelector={openRegionSelector}><SizeGuide /></MainLayout>} />
          <Route path="/privacy" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Privacy /></MainLayout>} />
          <Route path="/terms" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Terms /></MainLayout>} />
          <Route path="/sustainability" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Sustainability /></MainLayout>} />
          <Route path="/careers" element={<MainLayout onOpenRegionSelector={openRegionSelector}><Careers /></MainLayout>} />
        </Routes>
        <RegionSelector
          isOpen={showRegionSelector}
          onClose={() => setShowRegionSelector(false)}
        />
        <Toaster />
      </div>
    </BrowserRouter>
  );
};

function App() {
  return (
    <AuthProvider>
      <AdminModeProvider>
        <CurrencyProvider>
          <WishlistProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </WishlistProvider>
        </CurrencyProvider>
      </AdminModeProvider>
    </AuthProvider>
  );
}

export default App;